import { useState } from 'react';
import { useGetImports, useDeleteImport } from '../../../../serverState/imports';
import { ImportRecord } from '../../../../types/Import';
import { ImportUploadArea } from './ImportUploadArea';
import { ImportPreviousImports } from './ImportPreviousImports';
import { ImportDeleteConfirmModal } from './ImportDeleteConfirmModal';
import { ImportMappingScreen } from './ImportMappingScreen';
import { message } from 'antd';

interface SpaceSettingsImportTasksProps {
  workspaceId?: number;
  onCloseModal: () => void;
}

export const SpaceSettingsImportTasks = ({ workspaceId, onCloseModal }: SpaceSettingsImportTasksProps) => {
  const [activeImportId, setActiveImportId] = useState<number | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ImportRecord | null>(null);

  const importsQuery = useGetImports(workspaceId ?? null);
  const deleteMutation = useDeleteImport();

  const handleUploadSuccess = (importId: number) => {
    setActiveImportId(importId);
  };

  const handleProceedToMapping = (importId: number) => {
    setActiveImportId(importId);
  };

  const handleBackToList = () => {
    setActiveImportId(null);
  };

  const handleDeleteConfirm = () => {
    if (!deleteTarget) return;

    deleteMutation.mutate(
      { importId: deleteTarget.id },
      {
        onSuccess: () => {
          setDeleteTarget(null);
          message.success('Import deleted');
        },
        onError: (err) => {
          const errorMessage = (err as any)?.response?.data?.message || 'Failed to delete import';
          message.error(errorMessage);
        },
      }
    );
  };

  if (activeImportId) {
    return (
      <ImportMappingScreen
        importId={activeImportId}
        workspaceId={workspaceId!}
        onBack={handleBackToList}
        onCloseModal={onCloseModal}
      />
    );
  }

  return (
    // `box-border` is load-bearing: Tailwind preflight is off, so this div is content-box and
    // `py-6` would add 48px on top of `h-[556px]`, making the modal taller than every other tab.
    <div className="w-full h-[556px] box-border overflow-y-auto px-8 py-6">
      <div className="text-brand-grey-dark text-lg font-semibold mb-1">Import Tasks</div>
      <div className="text-neutral-700 text-sm mb-5">
        Import tasks from CSV or Excel files exported from other tools.
      </div>

      {workspaceId && (
        <>
          <ImportUploadArea workspaceId={workspaceId} onUploadSuccess={handleUploadSuccess} />
          <ImportPreviousImports
            imports={importsQuery.data}
            isLoading={importsQuery.isLoading}
            isError={importsQuery.isError}
            onProceedToMapping={handleProceedToMapping}
            onDelete={setDeleteTarget}
            onRetry={() => void importsQuery.refetch()}
          />
        </>
      )}

      <ImportDeleteConfirmModal
        open={!!deleteTarget}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteTarget(null)}
        confirmLoading={deleteMutation.isPending}
        tasksCount={deleteTarget?.tasksCreatedCount}
        createdByUser={deleteTarget?.createdByUser}
        createdAt={deleteTarget?.createdAt}
      />
    </div>
  );
};
